import { useEffect, useContext } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { PaymentMethodState, PaymentMethodStripeAch } from "@oysterhr/schema"
import { useAccountAspect } from "@oysterhr/identity/AccountAspectContext"
import { companyPaymentMethodsPath } from "@oysterhr/router"
import { ReusableWizard } from "@oysterhr/feature-components"
import { Pearl } from "@oysterhr/pearl"
import { PaymentMethodEnumType } from "~/app/utils/constants"
import { UrlQueryParamContext } from "@oysterhr/quarantine"
import { BankDetailsAdvisor } from "./components/BankDetailsAdvisor"
import { VerificationStep } from "./components/VerificationStep"
import { AuthorizationStep } from "./components/AuthorizationStep"
import { useStripeAchPaymentMethod } from "./hooks/useStripeAchPaymentMethod"
import ConnectPaymentMethodLayout from "../../../layouts/ConnectPaymentMethodLayout"

enum Step {
  Verification = "1",
  Authorization = "2",
}

const VerifyStripeAchPaymentMethod = () => {
  const { t } = useTranslation("company_interface/CompanyPaymentMethodsPage/AchBankDetailsFlow")
  const navigate = useNavigate()
  const { accountAspect: companyId } = useAccountAspect()
  const { data, loading } = useStripeAchPaymentMethod({ companyId })
  const paymentMethod = data?.company?.paymentMethod as PaymentMethodStripeAch | undefined
  const { params, setParam } = useContext(UrlQueryParamContext)
  const step = (params?.page ?? "1") as Step

  useEffect(() => {
    if (
      // When there're is no stripe ACH payment method for this company
      (!loading && paymentMethod?.__typename !== PaymentMethodEnumType.STRIPE_ACH) ||
      // When payment method is already authorized
      paymentMethod?.state === PaymentMethodState.AuthorizedForDirectDebit
    ) {
      return navigate(companyPaymentMethodsPath(companyId))
    }

    // When its already verified, Don't show verification step again
    if (paymentMethod?.state === PaymentMethodState.Verified && step === Step.Verification) {
      setParam("page", Step.Authorization)
    }
  }, [paymentMethod, loading, companyId, step, setParam, navigate])

  if (!paymentMethod) {
    return null
  }

  return (
    <ConnectPaymentMethodLayout title={t("title")}>
      <div className="text-left" style={{ width: 600 }}>
        <ReusableWizard scrollTopAfterTransition={true}>
          <ReusableWizard.Step
            title={t("verification.steps.verificationStep")}
            subtitle={`${t("nextStep")} ${t("verification.steps.authorizationStep")}`}
          >
            <VerificationStep paymentMethod={paymentMethod} />
          </ReusableWizard.Step>
          <ReusableWizard.Step title={t("verification.steps.authorizationStep")}>
            <AuthorizationStep paymentMethod={paymentMethod} />
          </ReusableWizard.Step>
        </ReusableWizard>
      </div>
      <Pearl name="ach-bank-details">
        <Pearl.SideBox>
          <BankDetailsAdvisor step={parseInt(step)} />
        </Pearl.SideBox>
      </Pearl>
    </ConnectPaymentMethodLayout>
  )
}

export default VerifyStripeAchPaymentMethod
